import { DateTime } from 'luxon'

import { IAutocompleteTag } from '@/common/models/data/dashboard/posts/tag'
import { IIdentifiable, ITimestamped } from '@/common/models/models'
import { IUserBasicData, UserBasicData } from '@/common/models/data/common/user'
import { AudioFile, IAudioFile } from '@/common/models/data/dashboard/media/audio-file'
import { Category, ICategory } from '@/common/models/data/dashboard/posts/category'
import { IImage, Image } from '@/common/models/data/dashboard/media/image'
import { Gallery, IGallery } from '@/common/models/data/dashboard/media/gallery'
import { IPostType, PostType } from '@/common/models/data/dashboard/posts/post-type'
import { ITagged, Tagged } from '@/common/models/data/dashboard/posts/tagged'

interface IPost extends IIdentifiable, ITimestamped {
    creator_id: number
    author_id: number
    type_id: number
    category_id: number | null

    slug: string
    title: string
    summary: string
    content: string
    image: string
    rating: number | null

    published: boolean
    approved: boolean
    published_at: string

    direct_url: string

    creator: IUserBasicData | null
    author: IUserBasicData
    type: IPostType
    category: ICategory | null
    tagged?: Array<ITagged>
    gallery: IGallery | null

    audio_files?: Array<IAudioFile>
    images?: Array<IImage>
}

class Post implements IPost {
    id = 0
    creator_id = 0
    author_id = 0
    type_id = 0
    category_id: number | null = null

    slug = ''
    title = ''
    summary = ''
    content = ''
    image = ''
    rating: number | null = null

    published = false
    approved = false
    published_at = DateTime.local().toFormat('yyyy-MM-dd')
    created_at = ''
    updated_at = ''

    direct_url = ''

    tags: Array<IAutocompleteTag> = []
    creator: UserBasicData | null = new UserBasicData()
    author = new UserBasicData()
    type = new PostType()
    category: Category | null = new Category()
    tagged: Array<Tagged> = []
    gallery: Gallery | null = null

    audio_files?: Array<AudioFile> = []
    images?: Array<IImage> = []

    file: File | null = null
    hasRating = false

    constructor (data?: IPost) {
        if (data !== undefined) {
            this.id = data.id
            this.creator_id = data.creator_id
            this.author_id = data.author_id
            this.type_id = data.type_id
            this.category_id = data.category_id

            this.slug = data.slug
            this.title = data.title
            this.summary = data.summary
            this.content = data.content
            this.image = data.image
            this.rating = data.rating
            this.hasRating = data.rating !== null

            this.published = data.published
            this.approved = data.approved
            this.published_at = data.published_at
            this.created_at = data.created_at
            this.updated_at = data.updated_at

            this.direct_url = data.direct_url

            this.tags = data.tagged?.map(tag => ({
                tag_name: tag.tag_name,
                count: tag.tag?.count // tag can be undefined, so better check it
            })) ?? []

            this.creator = data.creator ? new UserBasicData(data.creator) : null
            this.author = new UserBasicData(data.author)
            this.type = new PostType(data.type)
            this.category = data.category ? new Category(data.category) : null
            this.tagged = data.tagged?.map(tagged => new Tagged(tagged)) ?? []
            this.gallery = data.gallery ? new Gallery(data.gallery) : null

            this.audio_files = data.audio_files?.map(audioFile => new AudioFile(audioFile))
            this.images = data.images?.map(image => new Image(image))
        }
    }

    addAudioFile (audioFile: AudioFile): void {
        if (!this.audio_files) {
            this.audio_files = []
        }

        if (!this.audio_files.includes(audioFile)) {
            this.audio_files.push(audioFile)
        }
    }

    addImage (image: Image): void {
        if (!this.images) {
            this.images = []
        }

        if (!this.images.includes(image)) {
            this.images.push(image)
        }
    }

    get hasGallery (): boolean {
        return this.gallery !== null
    }

    get humanReadablePublishedAt (): string {
        return DateTime.fromISO(this.published_at).toFormat('d/MM/yyyy')
    }
}

export {
    IPost,
    Post
}
